import { DateTimeUnit, Duration } from "luxon";
import {
  DurationFormatter,
  RelativeTimeFormatPartUtils,
} from "shared-ts-ui-time";
import { useMemo } from "react";
import { AxisValueFormatterContext, XAxis } from "@mui/x-charts-premium";
import { resolveTimeAxisZoomSpanLimits } from "./zoom";
import { merge } from "lodash";
import { DurationSeriesMuiChartProps } from "./DurationSeriesMuiChart";
import { DurationWheel } from "shared-ts-utils-luxon";
import { useLocale } from "shared-ts-locale";

const scaleType = "band";

export const useXAxis = (
  xAxisMin: Duration,
  xAxisMax: Duration,
  timeUnit: DateTimeUnit,
  width: number | undefined,
  axisConfig?: XAxis,
  zoom?: DurationSeriesMuiChartProps["xAxisZoom"]
): Array<XAxis> => {
  const locale = useLocale();
  const durationFormatter = useMemo(
    () =>
      new DurationFormatter({
        locales: locale.baseName,
        options: {
          timeDirection: "none",
          style: "short",
        },
      }),
    [locale.baseName]
  );
  return useMemo(() => {
    const axisData: Array<Duration> = new DurationWheel({
      start: xAxisMin,
      timeUnit: "minute",
    }).runUntilDuration(xAxisMax, (duration) => duration.shiftToAll());

    const zoomSpanLimits = resolveTimeAxisZoomSpanLimits(
      xAxisMin,
      xAxisMax,
      timeUnit,
      width,
      zoom
    );
    const defaultXAxis: XAxis<"band"> = {
      id: "time",
      scaleType: scaleType,
      data: axisData,
      valueFormatter: valueFormatter(durationFormatter),
      tickInterval: tickInterval,
      zoom:
        zoomSpanLimits !== undefined
          ? {
              minSpan: zoomSpanLimits.minSpan,
              maxSpan: zoomSpanLimits.maxSpan,
            }
          : undefined,
    };
    const xAxis: XAxis = merge(defaultXAxis, axisConfig);

    return [xAxis];
  }, [
    axisConfig,
    durationFormatter,
    timeUnit,
    width,
    xAxisMax,
    xAxisMin,
    zoom,
  ]);
};

const tickInterval = (duration: Duration): boolean => {
  return duration.as("minutes") % 60 === 0;
};

const valueFormatter =
  (durationFormatter: DurationFormatter) =>
  (duration: Duration, context: AxisValueFormatterContext): string => {
    const parts = durationFormatter.formatToParts(duration);
    return RelativeTimeFormatPartUtils.toString(parts);
  };
